import isEmpty from 'lodash/isEmpty'

import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import { getContainersFromClients } from 'components/attacks/create/targets/target-utils'
import {
  getProvenanceByTargetType,
  isTargetParamsEmpty
} from 'components/services/service-utils'
import { naturalSortBy } from 'utils/list'

import type { Client, Container } from 'types/api/attacks/client'
import type { TargetingStrategy } from 'types/api/attacks/details/targeting'
import type { K8sObjectWithOwningTeamId } from 'types/api/kubernetes/kubernetes'
import type { AutocompleteSearchOption, TargetParams } from 'types/forms'

export const HOST_TARGETING_SECTION_COUNT = 4

/**
 * This is a list of tags that are sometimes generated when using dynamic hosts. We are forbidding the use of these tags
 * during host targeting because they are not guaranteed to be present at all times and can cause issues with running
 * reliability tests.
 */
export const HOST_TARGETING_FORBIDDEN_TAGS = [
  'local-hostname',
  'local-ip',
  'instance-id',
  'gremlin-client-version',
  'cloud',
  'os-name',
  'os-type'
]

// These are reversed so that when they are reversed later they
// are in the correct order.
export const HOST_TARGETING_SECTION_DEFAULT_NAMES = [
  'region',
  'zone',
  'service'
]

export const HOST_TARGETING_SECTION_DISPLAY_NAMES = {
  zone: 'Zone',
  region: 'Region',
  service: 'Service',
  other: 'Other Tags'
}

/**
 * These utility functions are sourced from functions of the same name found in
 * 'components/attacks/create/targets/target-utils.ts` and
 * 'components/attacks/create/targets/kubernetes/kubernetes-target-utils.ts'.
 *
 * They have been migrated to use TypeScript with new predefined types as well as refactored to eliminate
 * unnecessary parameters that are not utilized in the new RM Create Service workflow.
 */

function formatTargetParams(targetParams: TargetParams) {
  return Object.entries(targetParams).reduce((params, [key, values]) => {
    if (values.size === 0) return params // Value is empty; skip

    // Converting from Set() to array for API endpoint
    params[key] = Array.from(values)

    return params
  }, {})
}

/**
 * Returns a boolean indicating whether a Host or Container target is selected
 *
 * @param target
 * @param targetType
 * @param selectedTargets
 * @param selectedTargetParams
 * @param targetAll
 */
export const isTargetSelected = (
  target: Client,
  targetType: ProvenanceTargetType,
  selectedTargets: AutocompleteSearchOption[],
  selectedTargetParams: TargetParams,
  targetAll = false
) => {
  if (selectedTargets.length === 0) {
    return false
  }

  const params =
    (targetType === ProvenanceTargetType.ProcessData
      ? target.tags
      : target.labels) ?? {}

  // If there are no target params with selected values, all targets are considered selected
  // But we have switched our UI to show none selected by default to "start safe"
  // This is overriden later on with the "Target all hosts" option
  // This creates a default state of no targets being selected
  if (targetAll) {
    if (isTargetParamsEmpty(selectedTargetParams)) return true
  } else {
    if (isTargetParamsEmpty(selectedTargetParams)) return false
  }

  return Object.entries(selectedTargetParams).every(
    ([key, values = new Set()]) => {
      if (values instanceof Set) {
        if (values.size === 0) return true // skip empty sets
        return params[key] && values.has(params[key])
      } else {
        return params && params[key] === values
      }
    }
  )
}

/**
 * Returns a list of targetable services from a list of Clients.
 *
 * @param targets
 * @param targetType
 * @param selectedTargetParams
 */
export const formatTagsAndLabels = (
  targets: Client[],
  targetType: ProvenanceTargetType | undefined,
  selectedTargetParams: TargetParams | undefined
) => {
  let idKey: string
  let paramsKey: string
  let availableTargets: Container[] | Client[]

  if (targetType === ProvenanceTargetType.Container) {
    idKey = 'id'
    paramsKey = 'labels'
    availableTargets = getContainersFromClients(targets)
  } else {
    idKey = 'identifier'
    paramsKey = 'tags'
    availableTargets = targets
  }

  // Grab targets that match current selection
  const selectedTargets = availableTargets.filter((target) =>
    isTargetSelected(target, targetType, [], selectedTargetParams, false)
  )

  // Create singular set of alike tags/labels
  const combined = selectedTargets.reduce((combinedParams, target) => {
    const params: Client['tags'] | Container['labels'] = target[paramsKey]

    if (!params) return combinedParams

    Object.entries(params).forEach(([key, value]) => {
      if (!combinedParams[key]) {
        combinedParams[key] = new Set()
      }

      combinedParams[key].add(value)
    })

    return combinedParams
  }, {})

  const out = availableTargets.reduce(
    ({ keyValueSets, labelKeys }, target) => {
      const id: string = target[idKey]
      const params: Client['tags'] | Container['labels'] = target[paramsKey]

      if (!params) return { keyValueSets, labelKeys }

      Object.entries(params).forEach(([key, value]) => {
        // Filter out ephemeral tags
        if (HOST_TARGETING_FORBIDDEN_TAGS.includes(key)) {
          return
        }

        if (!labelKeys[key]) {
          labelKeys[key] = {
            name: key,
            targets: [],
            values: new Set(),
            disabled: new Set()
          }
        }

        // Add key-value to the set
        keyValueSets.add(`${key}:"${value}"`)
        labelKeys[key].values.add(value)
        labelKeys[key].targets.push(id)

        // If no target params have been selected yet; skip adding anything to the disabled list
        if (isTargetParamsEmpty(selectedTargetParams)) {
          return
        }

        if (combined[key] && combined[key].has(value)) {
          labelKeys[key].disabled.delete(value)
        } else {
          labelKeys[key].disabled.add(value)
        }
      })
      return { keyValueSets, labelKeys }
    },
    {
      keyValueSets: new Set<string>(),
      labelKeys: {}
    } as {
      keyValueSets: Set<string>
      labelKeys: {
        name: string
        targets: string[]
        values: Set<string>
        disabled: Set<string>
      }
    }
  )

  return { availableTargetParams: out.keyValueSets, labelKeys: out.labelKeys }
}

/**
 * Returns the clients that would be targeted for either host or container based attacks
 *
 * @param clients
 * @param targetType
 * @param selectedTargets
 * @param targetParams
 * @param targetAll
 * @returns {[]} selected clients
 */
export const getSelectedClients = (
  clients: Client[],
  targetType: ProvenanceTargetType,
  selectedTargets: AutocompleteSearchOption[],
  targetParams: TargetParams,
  targetAll?: boolean
) => {
  return clients.reduce((accum, target) => {
    if (targetType === ProvenanceTargetType.ProcessData) {
      const isClientSelected = isTargetSelected(
        target,
        targetType,
        selectedTargets,
        targetParams,
        targetAll
      )

      if (isClientSelected) {
        accum.push(target)
      }
    } else if (targetType === ProvenanceTargetType.Container) {
      const { containers } = target
      const isContainerSelected = containers.some((container) =>
        isTargetSelected(
          container,
          targetType,
          selectedTargets,
          targetParams,
          targetAll
        )
      )

      if (isContainerSelected) {
        accum.push(target)
      }
    }
    return accum
  }, [] as Client[])
}

/**
 * Returns correct tag/label format based on the type of selection strategy being used
 * @NOTE Currently based on `multiselection-targeting-enabled` LD feature flag
 */
export const getTargetParamsKey = (targetType) => {
  if (targetType === ProvenanceTargetType.ProcessData) {
    return 'multiSelectTags'
  } else if (targetType === ProvenanceTargetType.Container) {
    return 'multiSelectLabels'
  } else {
    return 'tags'
  }
}

/**
 * Returns a boolean indicating whether a Kubernetes target is selected.
 *
 * @param targets
 * @param selectedNamespace
 * @param selectedObjects
 */
export function getSelectedKubernetesTargets(
  targets: K8sObjectWithOwningTeamId[],
  {
    selectedNamespace,
    selectedObjects
  }: { selectedNamespace?: string; selectedObjects: Set<string> }
) {
  return targets.filter((t) => {
    const isTargetSelected = selectedObjects.has(t.uid)

    if (isTargetSelected) {
      if (selectedNamespace && selectedNamespace.toLowerCase() !== 'all') {
        return t.namespace === selectedNamespace
      }
    }

    return isTargetSelected
  })
}

/**
 * Returns a targetingStrategy for Host and/or Container target types
 */
export function getTargetingStrategyFromTargetParams(
  targetType: ProvenanceTargetType,
  targetParams?: TargetParams
): TargetingStrategy {
  const provenance = getProvenanceByTargetType(targetType)

  return {
    type: ProvenanceTargetType[provenance],
    ...(!isEmpty(targetParams) && {
      [getTargetParamsKey(targetType)]: formatTargetParams(targetParams)
    })
  }
}

export const sortSelectableServiceTargetParams = (
  labelKeys,
  defaults,
  numberOfSections
) => {
  const sortedLabelsByClientsLength = naturalSortBy(Object.keys(labelKeys), [
    // sort by defaults and then by number of unique values
    (label) => {
      // Using the indexOf we can ensure that if a label is a default it will
      // have a postive number and if not it will be `-1`
      return defaults.indexOf(label)
    },
    (label) => {
      // sort by the amount of unique values
      return labelKeys[label].values.size
    }
  ]).reverse()

  const splitLabels = sortedLabelsByClientsLength.reduce(
    ({ displayed, others }, labelKey) => {
      if (
        // Default label keys
        defaults.includes(labelKey) &&
        // Make sure we have one spot left over for "others"
        displayed.length < numberOfSections - 1
      ) {
        displayed.push(labelKey)
      } else {
        others.push(labelKey)
      }

      return {
        displayed,
        others
      }
    },
    {
      displayed: [],
      others: []
    }
  )

  return splitLabels
}

export function getTargetParamsFromSelection(
  option: AutocompleteSearchOption | AutocompleteSearchOption[],
  targetParams: TargetParams,
  selectedOptions: AutocompleteSearchOption[]
): TargetParams {
  // The MUI Autocomplete component with multiselect enabled returns an array of
  // selected options to the onChange function
  if (Array.isArray(option)) {
    return option.reduce((nextTargetParams, newValue) => {
      const { key, value } = newValue.params

      nextTargetParams[key] = nextTargetParams[key] || new Set()
      nextTargetParams[key].add(value)

      return nextTargetParams
    }, {})
  }

  const { key, value } = option.params

  const nextTargetParams = Object.assign({}, targetParams)
  nextTargetParams[key] = nextTargetParams[key] || new Set()

  const isNewOptionSelected = selectedOptions.some(
    (selectedOption) => selectedOption.params.value === value
  )

  if (isNewOptionSelected) {
    nextTargetParams[key].delete(value)
  } else {
    nextTargetParams[key].add(value)
  }

  return nextTargetParams
}

export function getSelectedOptions(
  optionsList: AutocompleteSearchOption[],
  targetParams: TargetParams
): AutocompleteSearchOption[] {
  const targetParamSectionsAndValues = {
    paramKeys: Object.keys(targetParams),
    paramValues: Object.values(targetParams).flatMap((targetParamValue) =>
      Array.from(targetParamValue)
    )
  }

  return optionsList.filter(
    (option) =>
      targetParamSectionsAndValues.paramKeys.includes(option.params.key) &&
      targetParamSectionsAndValues.paramValues.includes(option.params.value)
  )
}
